'use client';

import { useEffect, useRef } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { isRuHost } from '@/shared/lib/api-host';

/**
 * Yandex Metrica loader for the Russia mirror. Client-only.
 *
 * Same counter as the landing, on purpose. The landing and the app are two
 * halves of one journey (search result -> guide -> sign-up -> first render),
 * and only one counter can join them into a single visit chain; two counters
 * would show two anonymous halves that cannot be stitched. The counter has
 * getfacade.ru subdomains enabled for exactly this.
 *
 * Gated like ClarityAnalytics — production env only, so staging and local never
 * pollute the counter — plus the `.ru` host, because a Russian analytics service
 * has nothing to measure for a visitor in Berlin and everything to answer for
 * under GDPR. The id is public by design (it is in the snippet on every
 * Metrica-instrumented site), so it is hardcoded as a default with an env
 * override, exactly like the Clarity project id.
 *
 * Session replay (Вебвизор) is ON, turned on by the owner 2026-09-04.
 *
 * No masking markup is added, and that is a decision rather than an omission.
 * The only confidential input in this app is the password, and Metrica replaces
 * the contents of password and other fields it detects as confidential with
 * asterisks on its own, with no markup at all. Masking anything further —
 * `ym-disable-keys` on the auth forms was the version that shipped for ten
 * minutes — costs the e-mail and one-time-code keystrokes that make sign-up
 * drop-off debuggable, and buys nothing. Rendered house photos are not masked
 * either: they already live at permanent unsigned public URLs by product
 * design, so hiding them in a replay would protect nothing.
 */
export function YandexMetrica() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  // Seeded with the URL the counter reports itself on init; without it the
  // first render would report that same URL again and every session would open
  // with a phantom two-page visit.
  const reported = useRef<string | null>(null);

  const query = searchParams?.toString();
  const url = pathname ? `${pathname}${query ? `?${query}` : ''}` : null;

  useEffect(() => {
    if (process.env.NEXT_PUBLIC_APP_ENV !== 'production') return;
    if (!isRuHost()) return;

    const counterId = Number(process.env.NEXT_PUBLIC_YANDEX_METRICA_ID || '112289899');
    if (!counterId) return;

    const src = `https://mc.yandex.ru/metrika/tag.js?id=${counterId}`;
    // Guard against double-injection (React strict-mode double effect, remounts).
    if (document.querySelector(`script[src="${src}"]`)) return;

    // The queue stub must exist before tag.js loads: everything the counter is
    // told before the script is ready is replayed from window.ym.a.
    const w = window as typeof window & {
      ym?: ((...args: unknown[]) => void) & { a?: unknown[][]; l?: number };
    };
    const queue =
      w.ym ||
      Object.assign(
        (...args: unknown[]) => {
          queue.a = queue.a || [];
          queue.a.push(args);
        },
        { a: [] as unknown[][], l: 0 },
      );
    queue.l = Number(new Date());
    w.ym = queue;

    const script = document.createElement('script');
    script.async = true;
    script.src = src;
    document.head.appendChild(script);

    w.ym(counterId, 'init', {
      ssr: true,
      clickmap: true,
      webvisor: true,
      trackLinks: true,
      accurateTrackBounce: true,
    });

    if (reported.current === null) reported.current = url;
  }, [url]);

  useEffect(() => {
    if (process.env.NEXT_PUBLIC_APP_ENV !== 'production') return;
    if (!isRuHost()) return;
    if (!url || reported.current === url) return;

    const w = window as typeof window & { ym?: (...args: unknown[]) => void };
    if (!w.ym) return;

    reported.current = url;
    const counterId = Number(process.env.NEXT_PUBLIC_YANDEX_METRICA_ID || '112289899');
    w.ym(counterId, 'hit', `${location.origin}${url}`, { referer: document.referrer });
  }, [url]);

  return null;
}
